import { tokens } from "../theme";
import { Box, useTheme } from "@mui/material";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";

// Double-chevron toggle for resizable drawers and collapsible sidebars.
//
// isWide=false → << (left arrows, action expands / opens)
// isWide=true  → >> (right arrows, action collapses / closes)
//
// For sidebar usage pass isWide={collapsed} so the arrows always point in the
// direction the panel will move on click. `sx` carries ONLY what varies per
// caller — position, offset, z-index; everything else (size, border, color,
// hover/focus) lives in this component's own base so every drawer gets the
// same look for free.
//
// Straddles the panel's edge — half inside, half outside — matching the
// sidebar's own toggle: position:absolute, with `left`/`right` set to roughly
// minus half this button's width by the caller. This needs the panel's own
// Paper/container to have `overflow: "visible"` (MUI Drawer's Paper defaults
// to `overflow-y: auto`, which forces overflow-x to compute to `auto` too —
// per the CSS overflow spec, one axis can't stay `visible` if the other
// isn't — so anything positioned outside the box would otherwise be clipped,
// not just visually cut off at render).
const DrawerSnapToggle = ({
  enabled = true,
  isWide,
  onClick,
  ariaLabel = "Expand drawer",
  collapseLabel = "Collapse drawer to default width",
  ariaExpanded,
  sx,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const ACCENT = colors.link;
  if (!enabled) return null;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={isWide ? collapseLabel : ariaLabel}
      aria-pressed={ariaExpanded === undefined ? isWide : undefined}
      aria-expanded={ariaExpanded}
      sx={[
        {
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          p: 0,
          m: "auto",
          borderRadius: "999px",
          border: `1px solid ${colors.ctl}`,
          backgroundColor: colors.surface,
          color: colors.railMuted,
          boxShadow: "0 1px 4px rgba(16,24,40,.12)",
          cursor: "pointer",
          "&:hover": { color: ACCENT, borderColor: colors.ctlBorderHover },
          "&:focus-visible": {
            outline: `2px solid ${ACCENT}`,
            outlineOffset: "2px",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isWide ? (
        <KeyboardDoubleArrowRightRoundedIcon
          sx={{ fontSize: 18, fontWeight: 700 }}
        />
      ) : (
        <KeyboardDoubleArrowLeftRoundedIcon
          sx={{ fontSize: 18, fontWeight: 700 }}
        />
      )}
    </Box>
  );
};

export default DrawerSnapToggle;
